import { HttpErrorResponse } from '@angular/common/http';

// Pure function, no Angular DI — same convention as offline-cache-key.ts and
// offline-feature-gate.ts.
//
// "Did this request fail because we could not reach the API?" — as opposed to the API
// answering and saying no.
//
// Every offline fallback in this app used to test `status === 0` and nothing else. Status 0
// means no response arrived at all (hard offline, DNS failure, aborted CORS preflight), which
// is only ONE of the ways a network dies. A captive portal, a proxy, a load balancer in front
// of a slow or restarting API, or a dying mobile connection all return a real HTTP status —
// typically 502/503/504 — and a `status === 0` test treats those as a legitimate answer from
// the server. The consequence was the bug this was written for: offline on a flaky connection
// produced `504 Gateway Timeout` on School/getschoolconfig, the cached-bundle fallback in
// app.component.ts was skipped, startup failed, and a student with a fully downloaded lesson
// got the "SOMETHING WENT WRONG" screen instead of their saved content.
//
// OfflineConnectivityService's own header already names this exact trap: "navigator.onLine
// lies on captive portals and dying connections, the common case."
//
// Deliberately NOT included: 401/403 (the server answered, and the answer was no — falling
// back to cache there would paper over an expired session) and 4xx generally, apart from 408.
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof OfflineTimeoutError) return true;
  if (!(error instanceof HttpErrorResponse)) return false;

  switch (error.status) {
    case 0: // no response at all
    case 408: // request timeout
    case 502: // bad gateway
    case 503: // service unavailable
    case 504: // gateway timeout
      return true;
    default:
      return false;
  }
}

// Raised when a bootstrap call takes longer than we are willing to make the user wait.
export class OfflineTimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} did not respond within ${ms}ms`);
    this.name = 'OfflineTimeoutError';
  }
}

// D7 specifies "online-first with timeout, not a binary switch" — the timeout half was never
// implemented, so a dying connection meant waiting on the GATEWAY's timeout (tens of seconds,
// ending in the 504 this file exists because of) before any cached fallback was even tried.
//
// Deliberately applied only to the two bootstrap calls that gate whether the app renders at
// all, not globally in an interceptor: uploads and slow reports legitimately take minutes and
// a blanket timeout would break them.
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new OfflineTimeoutError(label, ms)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

// True when we already know, for free, that a request cannot succeed. navigator.onLine lies
// when it reports true (captive portals, dying connections), but it is reliable when it
// reports false — so this is only ever used to SKIP a request, never to decide one will work.
export function knownOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}
